import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { CREATOR_MESSAGE_REQUIRES_CAMPAIGN } from '../../utils/creatorMessageGate';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../../pages/BrandDashboard/brandDashboardTokens';

export interface CreatorMessageRequiresCampaignModalProps {
    open: boolean;
    onClose: () => void;
    /** Optional creator display name for copy. */
    creatorName?: string;
}

/**
 * Shown when public profile `can_message` is false.
 * CTA navigates to brand campaign create.
 */
export function CreatorMessageRequiresCampaignModal({
    open,
    onClose,
    creatorName,
}: CreatorMessageRequiresCampaignModalProps) {
    const navigate = useNavigate();

    const handleCreateCampaign = () => {
        onClose();
        navigate(ROUTES.BRAND_CAMPAIGN_NEW);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                    backgroundImage: 'none',
                },
            }}>
            <DialogContent sx={{ p: { xs: 3, sm: 3.5 }, position: 'relative' }}>
                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="Close"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: 'rgba(255,255,255,0.45)',
                        '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                    }}>
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 20,
                        color: '#fff',
                        letterSpacing: '-0.02em',
                        pr: 4,
                        mb: 1.5,
                    }}>
                    Active campaign required
                </Typography>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.55)',
                        lineHeight: 1.6,
                        mb: 3,
                    }}>
                    {CREATOR_MESSAGE_REQUIRES_CAMPAIGN}
                    {creatorName ? ` (${creatorName})` : ''}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Button
                        fullWidth
                        onClick={handleCreateCampaign}
                        sx={{
                            py: 1.5,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            bgcolor: BRAND_LUXE_PRIMARY,
                            color: '#392700',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#feb700' },
                        }}>
                        Create campaign
                    </Button>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={onClose}
                        sx={{
                            py: 1.25,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            borderColor: 'rgba(255,255,255,0.15)',
                            color: 'rgba(255,255,255,0.7)',
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                            '&:hover': {
                                borderColor: 'rgba(255,255,255,0.28)',
                                bgcolor: 'rgba(255,255,255,0.06)',
                            },
                        }}>
                        Not now
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default CreatorMessageRequiresCampaignModal;
